import React from 'react';
import { useTranslation } from 'react-i18next';
import { TablePaginationWrapper, ReloadIconButton } from '../../elements';
import useFeedRegistrations from '../../hooks/useFeedRegistrations';
import useFeedDailyTotals from '../../hooks/useFeedDailyTotals';
import { DEFAULT_LOCATION_PERIOD, FeedPeriod, feedPeriodRange } from '../../models/feed-registration';
import { DEFAULT_ROWS_PAGE_OPTIONS, DEFAULT_ROWS_PER_PAGE } from '../../constants/constants';
import FeedDailyChart from './FeedDailyChart';
import FeedPeriodSelect from './FeedPeriodSelect';
import FeedPeriodSummary from './FeedPeriodSummary';
import FeedRegistrationsTable from './FeedRegistrationsTable';

export interface LocationFeedTabProps {
  locationId: string;
}

/**
 * The feed a single pen received. Read-only - feed is registered from the mobile app during the
 * feeding round, and this is the farmer looking back at it afterwards.
 */
const LocationFeedTab: React.FC<LocationFeedTabProps> = ({ locationId }) => {
  const { t } = useTranslation();
  const [period, setPeriod] = React.useState<FeedPeriod>(DEFAULT_LOCATION_PERIOD);
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(DEFAULT_ROWS_PER_PAGE);
  const [chartReloadToken, setChartReloadToken] = React.useState(0);

  const { startDate, endDate } = feedPeriodRange(period);

  const locationIds = React.useMemo(() => (locationId ? [locationId] : []), [locationId]);

  const { registrations, totalCount, isBusy, loaded, reload } = useFeedRegistrations({
    locationIds,
    startDate,
    endDate,
    page,
    size: rowsPerPage
  });

  const { totals, loaded: totalsLoaded } = useFeedDailyTotals(locationIds, startDate, endDate, chartReloadToken);
  const chartEntries = React.useMemo(
    () => totals.map((total) => ({ date: total.date, feedName: total.feedName, grams: total.amountGrams })),
    [totals]
  );

  const reloadAll = () => {
    reload();
    setChartReloadToken((token) => token + 1);
  };

  const changePeriod = (next: FeedPeriod) => {
    setPeriod(next);
    setPage(0);
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
      <div className="bg-gray-50 px-6 py-4 border-b border-gray-200 flex flex-wrap items-center justify-between gap-3">
        <h2 className="text-base font-semibold text-gray-900 m-0">{t('f-feed-reg-loc-title')}</h2>
        <div className="flex items-center gap-3">
          <FeedPeriodSummary registrations={registrations} totalCount={totalCount} />
          <FeedPeriodSelect value={period} onChange={changePeriod} disabled={isBusy} />
          <ReloadIconButton action={reloadAll} />
        </div>
      </div>

      {totalsLoaded && chartEntries.length > 0 && (
        <div className="border-b border-gray-100">
          <FeedDailyChart
            entries={chartEntries}
            title={t('f-feed-reg-pen-per-day', { defaultValue: 'Feed per day' })}
            formatDayTotal={(amount) => t('f-feed-reg-total', { amount })}
          />
        </div>
      )}

      <div className="p-2">
        <TablePaginationWrapper
          rowsPerPageOptions={DEFAULT_ROWS_PAGE_OPTIONS}
          count={totalCount}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={(_event, newPage) => setPage(newPage)}
          onRowsPerPageChange={(event) => {
            setRowsPerPage(Number.parseInt(event.target.value, 10));
            setPage(0);
          }}
        >
          <FeedRegistrationsTable registrations={registrations} isBusy={isBusy} loaded={loaded} />
        </TablePaginationWrapper>
      </div>
    </div>
  );
};

export default LocationFeedTab;
